import React, { useEffect, useState } from 'react';
import { CalendarIcon, UserIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon, SparklesIcon, MapPinIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
type Activity = {
  date: string;
  deliveryLead: string;
  session: string;
  focus: string;
  imageUrl?: string;
  imageType: 'photo' | 'logo' | 'placeholder';
  initials?: string;
  isMandatory?: boolean;
  sessionFormat?: string;
};
type ItineraryEvent = {
  date: string;
  time?: string;
  title: string;
  location?: string;
  description: string;
  imageUrl: string;
  accentColor: string;
  rsvpUrl?: string;
};
type Investor = {
  name: string;
  firm: string;
  bio: string;
  imageUrl: string;
  accentColor: string;
};
type Founder = {
  name: string;
  company: string;
  imageUrl: string;
  websiteUrl: string;
  demoUrl: string;
};
type Outcome = {
  title: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  accentColor: string;
};
const activities: Activity[] = [{
  date: 'Week of January 12',
  deliveryLead: 'Bryan Duarte',
  session: 'Investor Readiness (1:1 Sessions)',
  focus: 'Personalized fundraising strategy, investor positioning, pitch refinement',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/2SVPCaSPdcuMiDCUTYJVNi/board-bryan-duarte.webp',
  imageType: 'photo',
  isMandatory: true,
  sessionFormat: '1:1 Sessions'
}, {
  date: 'January 19',
  deliveryLead: 'Chido Samantha Mbavarira',
  session: 'Sales On Steroids',
  focus: 'Partnerships, enterprise sales, corporate procurement pathways',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/qnuDzkr8qndZuV37wycVYH/Chido.png',
  imageType: 'photo',
  sessionFormat: 'Live Group Session'
}, {
  date: 'January 20',
  deliveryLead: 'Canada–Qatar Trade Commissioner Office & Qatar Development Bank',
  session: 'Pre-Industry Briefing Event',
  focus: 'Market entry insights, public–private partnerships, regional opportunity mapping',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/3wzMxZ2fwjYAH3RTybuSEK/QDB_LOGO.png',
  imageType: 'logo',
  isMandatory: true
}, {
  date: 'January 23',
  deliveryLead: 'Nada Shaheen',
  session: 'Investor Readiness & Corporate Strategy in MENA',
  focus: 'Capital strategy, regional investor expectations, scaling in the Middle East',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/4g3QxcdBSA9yMMybLmF9y7/Nada.jpg',
  imageType: 'photo',
  sessionFormat: 'Live Group Session'
}, {
  date: 'January 26',
  deliveryLead: 'Layla Freig',
  session: 'Financial Office Hours',
  focus: 'Financial modeling, metrics alignment, investor-grade financial storytelling',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/gUaEiNEnE8jd2vPJJ8c2VY/Layla_Freig.jpg',
  imageType: 'photo'
}, {
  date: 'January 29',
  deliveryLead: 'Tehmina Chaudhry',
  session: 'Mandatory Cultural Awareness Session',
  focus: 'Cultural intelligence, business etiquette, representation in Qatar and MENA markets',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/wA4EsNWLPD7oF4xVmQc1cL/Tehmina.jpg',
  imageType: 'photo',
  isMandatory: true
}];
const itineraryEvents: ItineraryEvent[] = [{
  date: 'Feb 1',
  time: '8:30pm - 10:00pm',
  title: 'Dinner at the Museum',
  location: 'National Museum of Qatar',
  description: 'Orientation + expectations setting',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/dQHESQ8jXs2k4Euq3vKp4V/Museum_of_Qatar.jpg',
  accentColor: 'from-orange-500 to-red-600'
}, {
  date: 'Feb 2',
  time: '8:00am',
  title: 'Women in Tech Breakfast',
  location: 'Presented by A Typical Ventures',
  description: 'Exclusive networking breakfast connecting women founders, investors, and tech leaders in the MENA ecosystem',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/cmoWkb8vsMGEphPkiV1HjV/Women_In_Tech_Breakfast.png',
  accentColor: 'from-amber-500 to-yellow-600',
  rsvpUrl: 'https://luma.com/210s5rtq?tk=zHevkE'
}, {
  date: 'Feb 2',
  time: 'All Day',
  title: 'Web Summit Qatar',
  location: '📍 Doha Exhibition & Convention Centre (DECC)',
  description: 'Global innovation conference and networking',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/upys6wVWeWAXRdFjehHnGK/Screenshot_2026-01-12_at_3.47.01_PM.png',
  accentColor: 'from-cyan-500 to-blue-600'
}, {
  date: 'Feb 2',
  time: '5:30pm - 8:30pm',
  title: 'QDB Garden Party - "Catalyzing Insights into Investments"',
  location: 'Presented by Qatar Development Bank',
  description: 'Investment reception and networking event',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/13KoLmy4ouU3sb5xZGFasu/Feb2_event_QDB.jpg',
  accentColor: 'from-violet-500 to-purple-600',
  rsvpUrl: 'https://www.eventbrite.com/e/qdb-investment-reception-web-summit-qatar-2026-tickets-1978818179443'
}, {
  date: 'Feb 3',
  time: '3:00pm - 5:30pm',
  title: 'Demo Day at Pullman Hotel',
  location: "Presented by FoundHers & Canadian Women's Foundation",
  description: 'Pitch your venture to investors and ecosystem partners',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/bk2XwH3ibxqDQt35zV2S6f/Pitch_Or_Party.jpg',
  accentColor: 'from-violet-500 to-purple-600',
  rsvpUrl: 'https://luma.com/9g08qbnf'
}, {
  date: 'Feb 3',
  time: '6:00pm - 8:00pm',
  title: 'Canadian Trade Commissioner Service Reception',
  location: 'Sheraton Grand Doha Resort & Convention Hotel, West Bay',
  description: "The Canadian Trade Commissioner Service (TCS) at the Embassy of Canada to the State of Qatar hosts a networking reception connecting Canadian delegates with key stakeholders from Qatar's innovation and technology ecosystem",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/vhMhER8nqpYxBgbxLjqnbD/EmbassyQatarCanada.jpg',
  accentColor: 'from-pink-500 to-rose-600'
}, {
  date: 'Feb 3',
  time: '7:00pm - 11:00pm',
  title: 'The Capital Capstone Social - Qatar',
  location: 'Raffles Doha',
  description: 'Shorooq x Rasmal x Dar Ventures networking social',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9JpSFf4eSrKP2GH5RJgsTa/RafflesDoha.png',
  accentColor: 'from-amber-500 to-orange-600',
  rsvpUrl: 'https://luma.com/4hhz8ae7'
}];
const investors: Investor[] = [{
  name: 'Nada Shaheen',
  firm: 'GB Ventures',
  bio: 'Nada Shaheen is a prominent investment expert and the Managing Director of GB Ventures, the corporate venture capital (CVC) arm of GB Corp, a leading industrial group in the MENA region. As of 2025, she is recognized as the first Egyptian woman to lead and manage a CVC fund in Africa. She oversees strategic investments in high-growth sectors, including fintech, mobility, logistics, and industrial tech. Under her leadership, GB Ventures invests between $500,000 and $5 million in Egyptian and African startups to drive corporate innovation and sustainable growth.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9RxbGSNSLZbitMuyDXTJd3/Nada.jpg',
  accentColor: 'from-violet-500 to-purple-600'
}, {
  name: 'Alina Truhina',
  firm: 'A Typical Ventures / Utopia Capital',
  bio: 'Alina is the CEO and Managing Partner of The Radical Fund, an early-stage VC fund investing in founders who are rapidly scaling adaptation and mitigation solutions to build climate resilience in the region and deliver commercial returns. Alina is also the Co-Founder of Founders Factory Africa, the pan-African venture studio and VC Fund headquartered in Johannesburg and operating across East, West and South Africa. Alina has raised over US$140million from LPs and investors for the Africa funds, which currently has over 60 portfolio ventures, and over 50 team members across the continent.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/hmscgVojdgdzm1i4ir5Yza/Alina.png',
  accentColor: 'from-cyan-500 to-blue-600'
}, {
  name: 'Ghadir Mahgoub',
  firm: 'Alchemist Doha',
  bio: 'Ghadir Mahgoub is Director of Investments at Alchemist Doha, where she leads venture capital strategy, portfolio development, and ecosystem growth to scale high-potential startups in Qatar and across emerging markets. With over a decade of experience across venture capital, investment banking, and asset management, she has driven funding strategies, valuations, and expansion plans for early- and growth-stage companies. She previously held senior investment roles at Qatar Development Bank, Commercial Bank, and the International Islamic Liquidity Management Corporation, building deep expertise in deal structuring, due diligence, and complex portfolios. Ghadir is focused on backing visionary founders, scaling disruptive models, and delivering strong portfolio performance across the region.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/74fhZrEb5WrbnpYnieiPNR/Ghadir.jpg',
  accentColor: 'from-rose-500 to-pink-600'
}, {
  name: 'Muhannad Taslaq',
  firm: 'Alchemist Doha',
  bio: 'Muhannad Taslaq is an investment leader and venture capitalist with experience across venture capital, angel investing, and venture building in MENA, the GCC, and emerging markets. He currently serves as Director of Investments at Alchemist Doha, where he leads early-stage and seed investments, supports portfolio construction, and partners closely with founders on product strategy, market entry, fundraising, and go-to-market execution. A former founder and ecosystem builder, he previously founded and scaled the Venture Lab at Princess Sumaya University for Technology into a leading university-based innovation hub and now serves as a Strategic Venture Building and Investment Advisor to Hussein Technical University, supporting the development of TheCore as a national engine for innovation. Across multiple venture labs, accelerators, and mentorship programs, he has contributed to the creation and support of over 50 startups in Jordan and the UK. Earlier, he founded and exited Mixed Dimensions, a deep-tech company at the intersection of gaming and 3D printing. His work in innovation and entrepreneurship has been recognized with the Silver Jubilee Medal awarded by His Majesty King Abdullah II of Jordan.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/iK8Ef2vgzpgTca1E3bQrkM/Muhannad.jpg',
  accentColor: 'from-indigo-500 to-violet-600'
}, {
  name: 'Tarik Sultan',
  firm: 'Builders VC MENA',
  bio: 'Tarik Sultan is a prominent investor and entrepreneur known for his work in the Middle East and the United States. Current Roles (2025): He serves as a Partner and Head of MENA (Middle East and North Africa) for Builders VC, where he focuses on investment strategy and startup scaling. Entrepreneurship: He is the Managing Partner of Sultan Ventures and founder of XLR8UH, a venture accelerator at the University of Hawaii.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/tKkyVcFeqFzyseZjqGFnPZ/Tarik.jpg',
  accentColor: 'from-amber-500 to-orange-600'
}, {
  name: 'William Bao Bean',
  firm: 'Orbit Ventures',
  bio: "William Bao Bean is Managing General Partner at Orbit Ventures and Venture Partner at SOSV, where he focuses on growth-stage investments in emerging markets. Since joining the VC ecosystem after senior roles at SingTel Innov8 Ventures and SoftBank China & India Holdings, he has backed standout companies such as Yodo1, GMobi and DemystData. Earlier in his career he spent over a decade as an equity research analyst at Deutsche Bank covering Asia's internet and telecom sectors. He holds a BA from Bowdoin College.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/jrz4vqh8j2zyJrJTUD68T4/william-bao-bean-profile.jpg',
  accentColor: 'from-pink-500 to-rose-600'
}, {
  name: 'Michael Lints',
  firm: 'Golden Gate Ventures',
  bio: 'Michael leads growth ventures at Golden Gate Ventures, managing LP fundraising and portfolio expansion for Series B and later-stage investments. Since 2013, he has raised over USD $60M, driven strategic acquisitions, and supported scaling across Southeast Asia. Previously, he founded a venture fund for Dutch SMEs and co-founded an IT managed services startup acquired by a major European telecom company.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9G2zx1PCm8e2cDkaZVqhaZ/MichaelLints.webp',
  accentColor: 'from-indigo-500 to-blue-600'
}, {
  name: 'Soumaya Ben Beya Dridje',
  firm: 'Rasmal Ventures',
  bio: "Soumaya Ben Beya Dridje is a Partner at Rasmal Ventures, the fund recently held its first close with over $30 million in commitments from investors including the Qatar Investment Authority (QIA), family offices, high-net-worth individuals, corporate investors, and Tunisia's ANAVA fund of funds. Rasmal Ventures invests in high-growth technology startups such as Carthagea, InstaDeep, Expensya, and SeekMake, focusing on fintech, health tech, B2B SaaS, and artificial intelligence from pre-Series A to Series B stages.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/wicqnXthK73mKq33epWPHb/soumaya.png',
  accentColor: 'from-emerald-500 to-teal-600'
}];
const outcomes: Outcome[] = [{
  title: 'Investor Readiness',
  description: 'Fundraising clarity, confidence, and readiness for global capital',
  icon: <TrendingUpIcon className="w-6 h-6" data-id="element-2" />,
  gradient: 'from-cyan-500 to-blue-600',
  accentColor: 'from-cyan-400 to-blue-500'
}, {
  title: 'Market Expansion',
  description: 'Practical insight into operating and selling in MENA markets',
  icon: <GlobeIcon className="w-6 h-6" data-id="element-3" />,
  gradient: 'from-violet-500 to-purple-600',
  accentColor: 'from-violet-400 to-purple-500'
}, {
  title: 'Global Positioning',
  description: 'Founder visibility and credibility at international platforms',
  icon: <SparklesIcon className="w-6 h-6" data-id="element-4" />,
  gradient: 'from-pink-500 to-rose-600',
  accentColor: 'from-pink-400 to-rose-500'
}, {
  title: 'Cultural Excellence',
  description: 'Confident, respectful, and informed engagement in Qatar',
  icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-5" />,
  gradient: 'from-amber-500 to-orange-600',
  accentColor: 'from-amber-400 to-orange-500'
}];
const founders: Founder[] = [{
  name: 'Fatima Abdullahi',
  company: 'Agent Flow',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/89GC66z5L2pps4A8zMq6FA/1.png',
  websiteUrl: 'https://www.agent-flow.ca/',
  demoUrl: 'https://www.canva.com/design/DAG7nzKEmWY/ZSu0-5fniyGPyIg7H1pdog/view?utm_content=DAG7nzKEmWY&utm_campaign=designshare&utm_medium=link&utm_source=recording_view'
}, {
  name: 'Igbagbo Ajagunna',
  company: 'CompCrafter',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/xix6nXF22Ar7H6KUASDxZN/2.png',
  websiteUrl: 'https://compcrafter.com/',
  demoUrl: 'https://www.canva.com/design/DAG-bMZ2804/bv_9OFSVl-8TSkrhiKjwmQ/watch?utm_content=DAG-bMZ2804&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h997b1da721'
}, {
  name: 'Sarra Ali',
  company: 'Diaminate',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/79yTjaq4GaUG4aaMg6qC5c/3.png',
  websiteUrl: 'https://diaminate.com/',
  demoUrl: 'https://www.loom.com/share/b8f9999935b34192bf9dad4100f8271b'
}, {
  name: 'Zainab Williams',
  company: 'Fund Evolve',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/6Z2ppwdMTD2ZP2RoRewfSY/4.png',
  websiteUrl: 'https://fundevolve.com/',
  demoUrl: 'https://www.canva.com/design/DAG7KMlS7aY/-z1LNxBfgbS-bjPDS0uWEg/view'
}, {
  name: 'Dr. Phyllis Pobee',
  company: 'GeneLean360',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/b9aeRHVTDTBPTSvmRRVrRm/5.png',
  websiteUrl: 'https://geneticweightloss.com/',
  demoUrl: 'https://www.canva.com/design/DAG7KMlS7aY/-z1LNxBfgbS-bjPDS0uWEg/view'
}, {
  name: 'Nanette Sene',
  company: 'Juno Technologies',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/kCGhCXZd2rHnZitCpQaHoC/6.png',
  websiteUrl: 'https://junotechno.com/',
  demoUrl: 'https://drive.google.com/file/d/1OfAO-tSh2-D3P9zspBwqJqkIW6Z6CGfY/view'
}, {
  name: 'Margarettha Pierre',
  company: 'KnowMedIQ',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/rmWcRh4MFDCEqjN6Subdpy/7.png',
  websiteUrl: 'https://knowmediq.ca/',
  demoUrl: 'https://www.canva.com/design/DAG77Hc8UJY/UebpdmeTuL093Jcas9BRwQ/view?utm_content=DAG77Hc8UJY&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h57437b8f2f'
}, {
  name: 'Lulu Mashonganyika',
  company: 'LiitCareConnect',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/8drqzDYVjmSNQ2TjaJAApf/8.png',
  websiteUrl: 'https://liitcareconnect.com',
  demoUrl: 'https://www.canva.com/design/DAG-gNoJgg4/A67PwpyS4U6sLKEg5u5vKQ/view?utm_content=DAG-gNoJgg4&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hc660110a53'
}, {
  name: 'Chioma Janelle',
  company: 'Oma Life',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9bWfY5gFrseqoNjRPauvDQ/9.png',
  websiteUrl: 'https://omalife.io/',
  demoUrl: 'https://www.canva.com/design/DAG5L3iUSM4/NEE3g5vLuZMIpWotvycEDg/view?utm_content=DAG5L3iUSM4&utm_campaign=designshare&utm_medium=link&utm_source=recording_view'
}, {
  name: 'Stephanie Martin',
  company: 'Talent IQ',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/oymYHCdHCTsHP9LWVmuNAV/10.png',
  websiteUrl: 'https://talentiqtech.work/',
  demoUrl: 'https://www.canva.com/design/DAG45y3fksU/ll8UIIyfWxynxT7cj9I8Rg/view?utm_content=DAG45y3fksU&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h3cec3620bc'
}];
function GeometricPattern() {
  return <svg className="absolute top-0 right-0 w-96 h-96 opacity-10" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6">
      {/* Hexagonal grid pattern inspired by Doha architecture */}
      <defs data-id="element-7">
        <linearGradient id="hexGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-8">
          <stop offset="0%" stopColor="#06b6d4" data-id="element-9" />
          <stop offset="100%" stopColor="#ec4899" data-id="element-10" />
        </linearGradient>
      </defs>
      {/* Large hexagon */}
      <polygon points="200,20 340,100 340,260 200,340 60,260 60,100" stroke="url(#hexGradient)" strokeWidth="1" fill="none" data-id="element-11" />
      {/* Medium hexagon */}
      <polygon points="200,60 300,120 300,240 200,300 100,240 100,120" stroke="url(#hexGradient)" strokeWidth="1" fill="none" data-id="element-12" />
      {/* Small hexagon */}
      <polygon points="200,100 260,140 260,220 200,260 140,220 140,140" stroke="url(#hexGradient)" strokeWidth="1" fill="none" data-id="element-13" />
      {/* Connecting lines */}
      <line x1="200" y1="20" x2="200" y2="100" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-14" />
      <line x1="340" y1="100" x2="260" y2="140" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-15" />
      <line x1="340" y1="260" x2="260" y2="220" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-16" />
      <line x1="200" y1="340" x2="200" y2="260" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-17" />
      <line x1="60" y1="260" x2="140" y2="220" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-18" />
      <line x1="60" y1="100" x2="140" y2="140" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-19" />
      {/* Decorative dots */}
      <circle cx="200" cy="180" r="4" fill="url(#hexGradient)" data-id="element-20" />
      <circle cx="200" cy="20" r="3" fill="#06b6d4" data-id="element-21" />
      <circle cx="340" cy="100" r="3" fill="#ec4899" data-id="element-22" />
      <circle cx="340" cy="260" r="3" fill="#06b6d4" data-id="element-23" />
      <circle cx="200" cy="340" r="3" fill="#ec4899" data-id="element-24" />
      <circle cx="60" cy="260" r="3" fill="#06b6d4" data-id="element-25" />
      <circle cx="60" cy="100" r="3" fill="#ec4899" data-id="element-26" />
    </svg>;
}
function GeometricPatternLeft() {
  return <svg className="absolute bottom-0 left-0 w-64 h-64 opacity-5" viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-27">
      <defs data-id="element-28">
        <linearGradient id="hexGradient2" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-29">
          <stop offset="0%" stopColor="#ec4899" data-id="element-30" />
          <stop offset="100%" stopColor="#06b6d4" data-id="element-31" />
        </linearGradient>
      </defs>
      <polygon points="150,30 250,90 250,210 150,270 50,210 50,90" stroke="url(#hexGradient2)" strokeWidth="1.5" fill="none" data-id="element-32" />
      <polygon points="150,70 210,110 210,190 150,230 90,190 90,110" stroke="url(#hexGradient2)" strokeWidth="1" fill="none" data-id="element-33" />
    </svg>;
}
function TabNavigation({
  activeTab,
  setActiveTab
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  return <div className="flex justify-center mb-12" data-id="element-34">
      <div className="inline-flex bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-1.5 flex-wrap gap-1" data-id="element-35">
        <button onClick={() => setActiveTab('growth-labs')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'growth-labs' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-36">
          Growth Labs
        </button>
        <button onClick={() => setActiveTab('foundhers-delegation')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'foundhers-delegation' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-37">
          FoundHers+ Delegation
        </button>
        <button onClick={() => setActiveTab('doha-itinerary')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'doha-itinerary' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-38">
          Doha Itinerary
        </button>
        <button onClick={() => setActiveTab('qatar-ecosystem')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'qatar-ecosystem' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-39">
          Qatar Ecosystem
        </button>
      </div>
    </div>;
}
function ActivityCard({
  activity,
  index
}: {
  activity: Activity;
  index: number;
}) {
  const isEven = index % 2 === 0;
  // Determine gradient based on activity type and specific facilitators
  const getGradient = () => {
    // Special colors for specific facilitators
    if (activity.deliveryLead === 'Chido Samantha Mbavarira') return 'from-pink-500 to-rose-600';
    if (activity.deliveryLead === 'Layla Freig') return 'from-pink-500 to-rose-600';
    // Default colors based on session type
    if (activity.isMandatory) return 'from-emerald-500 to-green-600';
    if (activity.sessionFormat === '1:1 Sessions') return 'from-violet-500 to-purple-600';
    if (activity.sessionFormat === 'Live Group Session') return 'from-cyan-500 to-blue-600';
    return 'from-amber-500 to-orange-600';
  };
  const isBryanDuarte = activity.deliveryLead === 'Bryan Duarte';
  return <div className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 items-start`} data-id="element-40">
      {/* Facilitator Image */}
      <div className="flex-shrink-0 w-full md:w-48" data-id="element-41">
        <div className="relative" data-id="element-42">
          {/* Gradient border */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${getGradient()} rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500`} data-id="element-43"></div>

          {/* Image container */}
          <div className="relative rounded-2xl overflow-hidden h-48 bg-slate-800" data-id="element-44">
            {activity.imageUrl ? <img src={activity.imageUrl} alt={activity.deliveryLead} className={`w-full h-full ${activity.imageType === 'logo' ? 'object-contain p-6' : 'object-cover object-top'}`} data-id="element-45" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800" data-id="element-46">
                <span className="text-4xl font-bold text-white" data-id="element-47">
                  {activity.initials}
                </span>
              </div>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1" data-id="element-48">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300 h-full" data-id="element-49">
          {/* Date and badges */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-50">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${getGradient()} bg-opacity-20 border border-white/20`} data-id="element-51">
              <CalendarIcon className="w-4 h-4 text-white" data-id="element-52" />
              <span className="text-sm font-semibold text-white" data-id="element-53">
                {activity.date}
              </span>
            </div>
            {activity.isMandatory && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30" data-id="element-54">
                <span className="text-xs font-bold text-emerald-300" data-id="element-55">
                  MANDATORY
                </span>
              </div>}
            {activity.sessionFormat && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-500/30" data-id="element-56">
                <span className="text-xs font-semibold text-cyan-300" data-id="element-57">
                  {activity.sessionFormat}
                </span>
              </div>}
          </div>

          {/* Session title */}
          <h3 className="text-xl font-bold text-white mb-2 [text-wrap:balance]" data-id="element-58">
            {activity.session}
          </h3>

          {/* Delivery lead */}
          <div className="flex items-center gap-2 mb-3" data-id="element-59">
            <UserIcon className="w-4 h-4 text-violet-400" data-id="element-60" />
            <span className="text-sm text-violet-300 font-medium" data-id="element-61">
              {activity.deliveryLead}
            </span>
          </div>

          {/* Focus description */}
          <p className="text-slate-300 leading-relaxed [text-wrap:pretty] mb-4" data-id="element-62">
            {activity.focus}
          </p>

          {/* Book Now button for Bryan Duarte */}
          {isBryanDuarte && <a href="https://calendly.com/bryanduarte/foundhers_45-minute-mvp_evaluation_with_bryan" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold hover:from-violet-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-violet-500/50" data-id="element-63">
              <CalendarIcon className="w-4 h-4" data-id="element-64" />
              <span data-id="element-65">Book Now</span>
            </a>}

          {/* Decorative accent line */}
          <div className={`mt-4 h-1 w-20 bg-gradient-to-r ${getGradient()} rounded-full`} data-id="element-66"></div>
        </div>
      </div>
    </div>;
}
function ItineraryCard({
  event,
  index
}: {
  event: ItineraryEvent;
  index: number;
}) {
  const isEven = index % 2 === 0;
  return <div className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`} data-id="element-67">
      {/* Image with futuristic border */}
      <div className="flex-shrink-0 w-full md:w-1/2" data-id="element-68">
        <div className="relative" data-id="element-69">
          {/* Animated gradient border */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${event.accentColor} rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-500`} data-id="element-70"></div>

          {/* Image container */}
          <div className="relative rounded-3xl overflow-hidden h-80" data-id="element-71">
            <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-72" />
            {/* Gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-60`} data-id="element-73"></div>

            {/* Date badge floating on image */}
            <div className="absolute bottom-6 right-6" data-id="element-74">
              <div className={`bg-gradient-to-r ${event.accentColor} rounded-2xl px-6 py-3 shadow-2xl`} data-id="element-75">
                <span className="text-2xl font-bold text-white" data-id="element-76">
                  {event.date}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full md:w-1/2" data-id="element-77">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-78">
          {/* Time badge */}
          {event.time && <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 mb-4" data-id="element-79">
              <ClockIcon className="w-4 h-4 text-cyan-300" data-id="element-80" />
              <span className="text-sm font-semibold text-cyan-300" data-id="element-81">
                {event.time}
              </span>
            </div>}

          {/* Title */}
          <h3 className="text-3xl font-bold text-white mb-4 leading-tight [text-wrap:balance]" data-id="element-82">
            {event.title}
          </h3>

          {/* Location */}
          {event.location && <div className="flex items-start gap-3 mb-4" data-id="element-83">
              <MapPinIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-84" />
              <span className="text-lg text-violet-300 font-medium [text-wrap:balance]" data-id="element-85">
                {event.location}
              </span>
            </div>}

          {/* Description */}
          <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty] mb-6" data-id="element-86">
            {event.description}
          </p>

          {/* RSVP Button */}
          {event.rsvpUrl && <a href={event.rsvpUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-600 hover:to-violet-700 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-cyan-500/50" data-id="element-87">
              <CheckCircleIcon className="w-5 h-5" data-id="element-88" />
              <span data-id="element-89">RSVP Here</span>
            </a>}

          {/* Decorative accent line */}
          <div className={`mt-6 h-1 w-24 bg-gradient-to-r ${event.accentColor} rounded-full`} data-id="element-90"></div>
        </div>
      </div>
    </div>;
}
function CityTourCard() {
  return <div className="group relative" data-id="element-91">
      <div className="relative flex flex-col gap-8" data-id="element-92">
        {/* Content card */}
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-93">
          {/* Time badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 mb-4" data-id="element-94">
            <ClockIcon className="w-4 h-4 text-cyan-300" data-id="element-95" />
            <span className="text-sm font-semibold text-cyan-300" data-id="element-96">
              Morning & Afternoon
            </span>
          </div>

          {/* Date badge */}
          <div className="inline-flex items-center gap-3 mb-6" data-id="element-97">
            <div className="bg-gradient-to-r from-pink-500 to-rose-600 rounded-2xl px-6 py-3 shadow-2xl" data-id="element-98">
              <span className="text-2xl font-bold text-white" data-id="element-99">Feb 4</span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-3xl font-bold text-white mb-4 leading-tight" data-id="element-100">
            Meeting at Trade Commissioner's Office & City Tour
          </h3>

          {/* Location */}
          <div className="flex items-start gap-3 mb-4" data-id="element-101">
            <MapPinIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-102" />
            <span className="text-lg text-violet-300 font-medium" data-id="element-103">
              Doha City
            </span>
          </div>

          {/* Description */}
          <p className="text-slate-300 leading-relaxed text-lg mb-8" data-id="element-104">
            Strategic meetings followed by cultural immersion and city
            exploration
          </p>

          {/* Image grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-105">
            {/* QDB Building */}
            <div className="relative" data-id="element-106">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-violet-400 to-cyan-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-107"></div>
              <div className="relative rounded-2xl overflow-hidden h-48" data-id="element-108">
                <img src="https://cdn.magicpatterns.com/uploads/ip5kngqFtgMiH91zTFMdsH/qatar-development-bank-head-office5-1651747582.webp" alt="Qatar Development Bank" className="w-full h-full object-cover" data-id="element-109" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-110"></div>
                <div className="absolute bottom-3 left-3 right-3" data-id="element-111">
                  <p className="text-white text-sm font-semibold" data-id="element-112">
                    Qatar Development Bank
                  </p>
                </div>
              </div>
            </div>

            {/* Doha Skyline */}
            <div className="relative" data-id="element-113">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-violet-400 to-pink-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-114"></div>
              <div className="relative rounded-2xl overflow-hidden h-48" data-id="element-115">
                <img src="https://cdn.magicpatterns.com/uploads/nEjauh7qKyAfaNhFCjRepb/temp_file_Qatar_-_Doha_-_NS1-700x467.jpg" alt="Doha Skyline" className="w-full h-full object-cover" data-id="element-116" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-117"></div>
                <div className="absolute bottom-3 left-3 right-3" data-id="element-118">
                  <p className="text-white text-sm font-semibold" data-id="element-119">
                    Doha Skyline
                  </p>
                </div>
              </div>
            </div>

            {/* Luxury Vehicle Tour */}
            <div className="relative" data-id="element-120">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-pink-400 to-cyan-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-121"></div>
              <div className="relative rounded-2xl overflow-hidden h-48" data-id="element-122">
                <img src="https://cdn.magicpatterns.com/uploads/qA9vJ1Q5iM5CwXSofrqnTo/8_Best_SUVs_in_Qatar_Title_image_cover.jpg" alt="Luxury City Tour" className="w-full h-full object-cover" data-id="element-123" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-124"></div>
                <div className="absolute bottom-3 left-3 right-3" data-id="element-125">
                  <p className="text-white text-sm font-semibold" data-id="element-126">
                    Luxury City Tour
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative accent line */}
          <div className="mt-6 h-1 w-24 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full" data-id="element-127"></div>
        </div>
      </div>
    </div>;
}
function InvestorCard({
  investor,
  index
}: {
  investor: Investor;
  index: number;
}) {
  const isEven = index % 2 === 0;
  return <div className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-start`} data-id="element-128">
      {/* Photo */}
      <div className="flex-shrink-0 w-full md:w-80" data-id="element-129">
        <div className="relative" data-id="element-130">
          {/* Gradient border */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${investor.accentColor} rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-500`} data-id="element-131"></div>

          {/* Image container */}
          <div className="relative rounded-3xl overflow-hidden h-96 bg-slate-800" data-id="element-132">
            <img src={investor.imageUrl} alt={investor.name} className="w-full h-full object-cover object-top" data-id="element-133" />
          </div>
        </div>
      </div>

      {/* Bio content */}
      <div className="flex-1" data-id="element-134">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/8 hover:border-white/20 transition-all duration-300 h-full" data-id="element-135">
          {/* Name and firm */}
          <div className="mb-6" data-id="element-136">
            <h3 className="text-3xl font-bold text-white mb-2 [text-wrap:balance]" data-id="element-137">
              {investor.name}
            </h3>
            <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${investor.accentColor} bg-opacity-20 border border-white/20`} data-id="element-138">
              <span className="text-sm font-semibold text-white [text-wrap:balance]" data-id="element-139">
                {investor.firm}
              </span>
            </div>
          </div>

          {/* Bio */}
          <p className="text-slate-300 leading-relaxed text-base [text-wrap:pretty]" data-id="element-140">
            {investor.bio}
          </p>

          {/* Decorative accent line */}
          <div className={`mt-6 h-1 w-24 bg-gradient-to-r ${investor.accentColor} rounded-full`} data-id="element-141"></div>
        </div>
      </div>
    </div>;
}
function OutcomeCard({
  outcome
}: {
  outcome: Outcome;
}) {
  return <div className="group relative" data-id="element-142">
      {/* Animated gradient border */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${outcome.gradient} rounded-2xl blur opacity-60 group-hover:opacity-100 transition duration-500`} data-id="element-143"></div>

      <div className="relative bg-slate-900/90 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-slate-900/70 transition-all duration-300 h-full" data-id="element-144">
        {/* Icon with gradient background */}
        <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${outcome.gradient} flex items-center justify-center mb-4 shadow-lg`} data-id="element-145">
          {/* Glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-br ${outcome.gradient} rounded-xl blur-md opacity-50`} data-id="element-146"></div>
          <div className="relative text-white" data-id="element-147">{outcome.icon}</div>
        </div>

        {/* Title with gradient accent */}
        <h3 className="text-xl font-bold text-white mb-3 [text-wrap:balance]" data-id="element-148">
          {outcome.title}
        </h3>

        {/* Description */}
        <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-149">
          {outcome.description}
        </p>

        {/* Decorative accent line with gradient */}
        <div className={`mt-5 h-1 w-24 bg-gradient-to-r ${outcome.accentColor} rounded-full shadow-lg`} data-id="element-150"></div>

        {/* Decorative corner accent */}
        <div className={`absolute top-4 right-4 w-8 h-8 bg-gradient-to-br ${outcome.gradient} opacity-10 rounded-full blur-sm`} data-id="element-151"></div>
      </div>
    </div>;
}
function FounderCard({
  founder
}: {
  founder: Founder;
}) {
  return <div className="group relative flex flex-col items-center" data-id="element-152">
      {/* Founder Image - transparent background that blends into page gradient */}
      <div className="relative w-full aspect-[3/4] overflow-hidden mb-4" data-id="element-153">
        <img src={founder.imageUrl} alt={founder.company} className="w-full h-full object-contain transform group-hover:scale-105 transition duration-500" data-id="element-154" />
      </div>

      {/* Company Logo Placeholder - you can add actual logos here */}
      <div className="mb-2" data-id="element-155">
        <div className="text-center" data-id="element-156">
          <p className="text-lg font-bold text-white mb-1" data-id="element-157">{founder.company}</p>
          <p className="text-sm text-slate-300" data-id="element-158">{founder.name}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-2 w-full mt-2" data-id="element-159">
        <a href={founder.websiteUrl} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold text-center transition-all duration-300" data-id="element-160">
          Visit Website
        </a>
        <a href={founder.demoUrl} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-600 hover:to-violet-700 text-white text-sm font-semibold text-center transition-all duration-300 shadow-lg hover:shadow-cyan-500/50" data-id="element-161">
          View Demo
        </a>
      </div>
    </div>;
}
function FoundHersDelegation() {
  return <div className="space-y-16" data-id="element-162">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-163">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/20 to-violet-500/20 border border-pink-500/30 mb-6" data-id="element-164">
          <SparklesIcon className="w-4 h-4 text-pink-300" data-id="element-165" />
          <span className="text-sm font-semibold text-pink-300" data-id="element-166">
            Women-Led Innovation
          </span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-167">
          The FoundHers+ Delegation
        </h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto [text-wrap:balance]" data-id="element-168">
          A powerful cohort of women-led startups redefining innovation across
          global markets
        </p>
      </div>

      {/* Delegation Description */}
      <section data-id="element-169">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-170">
          <div className="flex items-center gap-3 mb-6" data-id="element-171">
            <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-172" />
            <h2 className="text-2xl font-bold text-white" data-id="element-173">
              About the Delegation
            </h2>
          </div>
          <div className="space-y-4 text-slate-300 leading-relaxed text-lg" data-id="element-174">
            <p className="[text-wrap:pretty]" data-id="element-175">
              The FoundHers delegation brings together a powerful cohort of
              women-led startups operating at the forefront of innovation across
              SaaS, FinTech, HealthTech, MedTech, Cleantech, HR Tech, and
              Defence Tech. These founders represent scalable, impact-driven
              businesses building solutions for global markets, with strong
              traction, ambitious growth plans, and a commitment to inclusive
              innovation.
            </p>
            <p className="[text-wrap:pretty]" data-id="element-176">
              Together, this delegation reflects the depth and diversity of the
              FoundHers ecosystem, companies solving real-world problems through
              technology, data, and design, while redefining leadership in their
              respective industries.
            </p>
          </div>
        </div>
      </section>

      {/* Founders Grid */}
      <section data-id="element-177">
        <div className="flex items-center gap-3 mb-8" data-id="element-178">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-179" />
          <h2 className="text-2xl font-bold text-white" data-id="element-180">Meet the Founders</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-181">
          {founders.map((founder, index) => <FounderCard key={index} founder={founder} data-id="element-182" />)}
        </div>
      </section>

      {/* Industry Sectors */}
      <section data-id="element-183">
        <div className="flex items-center gap-3 mb-8" data-id="element-184">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-185" />
          <h2 className="text-2xl font-bold text-white" data-id="element-186">
            Industry Focus Areas
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-187">
          {/* SaaS */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-188">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4" data-id="element-189">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-190" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-191">SaaS</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-192">
              Software-as-a-Service platforms driving digital transformation
            </p>
          </div>

          {/* FinTech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-193">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4" data-id="element-194">
              <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-195" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-196">FinTech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-197">
              Financial technology solutions revolutionizing banking and
              payments
            </p>
          </div>

          {/* HealthTech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-198">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4" data-id="element-199">
              <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-200" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-201">HealthTech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-202">
              Digital health solutions improving patient care and outcomes
            </p>
          </div>

          {/* MedTech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-203">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4" data-id="element-204">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-205" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-206">MedTech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-207">
              Medical technology innovations advancing healthcare delivery
            </p>
          </div>

          {/* Cleantech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-208">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4" data-id="element-209">
              <GlobeIcon className="w-6 h-6 text-white" data-id="element-210" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-211">Cleantech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-212">
              Sustainable technology solutions for climate and energy challenges
            </p>
          </div>

          {/* HR Tech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-213">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center mb-4" data-id="element-214">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-215" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-216">HR Tech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-217">
              Workforce technology transforming talent management and operations
            </p>
          </div>
        </div>
      </section>

      {/* Closing section */}
      <div className="mt-16 text-center" data-id="element-218">
        <div className="bg-gradient-to-r from-pink-500/10 via-violet-500/10 to-cyan-500/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10" data-id="element-219">
          <h3 className="text-2xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-220">
            Building the Future Together
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto [text-wrap:pretty]" data-id="element-221">
            This delegation represents the next generation of women-led
            innovation, combining technical excellence with market insight and a
            commitment to creating lasting impact across industries and
            geographies.
          </p>
        </div>
      </div>
    </div>;
}
function DohaItinerary() {
  // Group events by date
  const eventsByDate = itineraryEvents.reduce((acc, event) => {
    if (!acc[event.date]) {
      acc[event.date] = [];
    }
    acc[event.date].push(event);
    return acc;
  }, {} as Record<string, ItineraryEvent[]>);
  return <div className="space-y-16" data-id="element-222">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-223">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-224">
          Your Doha Journey
        </h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto [text-wrap:balance]" data-id="element-225">
          Four days of innovation, culture, and strategic connections in the
          heart of Qatar
        </p>
      </div>

      {/* Accommodation Section */}
      <section data-id="element-226">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-227">
          <div className="flex items-center gap-3 mb-6" data-id="element-228">
            <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full" data-id="element-229" />
            <h2 className="text-2xl font-bold text-white" data-id="element-230">
              Accommodation & Logistics
            </h2>
          </div>

          <div className="space-y-6 mb-8" data-id="element-231">
            <div className="space-y-4 text-slate-300 leading-relaxed text-lg" data-id="element-232">
              <p className="[text-wrap:pretty]" data-id="element-233">
                All guests are expected to arrive in Doha by{' '}
                <span className="text-white font-semibold" data-id="element-234">February 1</span>,
                with check-out scheduled for{' '}
                <span className="text-white font-semibold" data-id="element-235">February 5</span>.
                Programming will take place throughout this period, including
                planned meetings, ecosystem engagements, and Demo Day
                activities.
              </p>
              <p className="[text-wrap:pretty]" data-id="element-236">
                Participants will be staying at{' '}
                <span className="text-cyan-300 font-semibold" data-id="element-237">
                  Millennium Plaza Doha
                </span>
                , a modern five-star hotel located in the Al Sadd district. The
                hotel is approximately 5–6 km (10–15 minutes by car) from the
                Doha Exhibition and Convention Center (DECC), allowing for
                convenient daily transfers to and from all official program
                activities.
              </p>
              <p className="[text-wrap:pretty]" data-id="element-238">
                Millennium Plaza Doha offers a comfortable and professional stay
                experience, well suited for business-focused delegations. Guests
                can expect spacious, well-appointed rooms, reliable service, and
                a calm environment that supports both productivity and rest. The
                hotel features multiple dining options, a fitness center, spa
                facilities, and well-equipped meeting spaces, providing
                everything needed for a seamless and enjoyable stay during the
                program.
              </p>
            </div>
          </div>

          {/* Hotel Images Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-239">
            {/* Hotel Lobby */}
            <div className="relative group" data-id="element-240">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-400 to-violet-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-241"></div>
              <div className="relative rounded-2xl overflow-hidden h-64" data-id="element-242">
                <img src="https://cdn.magicpatterns.com/uploads/aQsryndzJCyosQ1i9FHmU9/Millennium-Plaza-Hotel-Doha-Lobby.webp" alt="Millennium Plaza Doha Lobby" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-243" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-244"></div>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-245">
                  <p className="text-white text-sm font-semibold" data-id="element-246">
                    Hotel Lobby
                  </p>
                </div>
              </div>
            </div>

            {/* Hotel Interior */}
            <div className="relative group" data-id="element-247">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-pink-400 to-orange-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-248"></div>
              <div className="relative rounded-2xl overflow-hidden h-64" data-id="element-249">
                <img src="https://cdn.magicpatterns.com/uploads/j4km9rXxqbUy3d1LxCX4m3/medium-1280px.jpg" alt="Millennium Plaza Doha Interior" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-250" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-251"></div>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-252">
                  <p className="text-white text-sm font-semibold" data-id="element-253">
                    Hotel Interior
                  </p>
                </div>
              </div>
            </div>

            {/* Guest Room */}
            <div className="relative group" data-id="element-254">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-rose-400 to-cyan-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-255"></div>
              <div className="relative rounded-2xl overflow-hidden h-64" data-id="element-256">
                <img src="https://cdn.magicpatterns.com/uploads/q29xjUTf3mCLM14XRaRpTT/631084380.jpg" alt="Millennium Plaza Doha Guest Room" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-257" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-258"></div>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-259">
                  <p className="text-white text-sm font-semibold" data-id="element-260">Guest Room</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary events grouped by day */}
      <section data-id="element-261">
        <div className="flex items-center gap-3 mb-12" data-id="element-262">
          <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-263" />
          <h2 className="text-2xl font-bold text-white" data-id="element-264">Daily Schedule</h2>
        </div>

        <div className="space-y-24" data-id="element-265">
          {Object.entries(eventsByDate).map(([date, events], dayIndex) => <div key={date} data-id="element-266">
              {/* Day Header */}
              <div className="mb-12" data-id="element-267">
                <div className="flex items-center gap-4 mb-4" data-id="element-268">
                  <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 via-violet-500 to-pink-500 flex items-center justify-center shadow-2xl" data-id="element-269">
                    <span className="text-2xl font-bold text-white" data-id="element-270">
                      {date.split(' ')[1]}
                    </span>
                  </div>
                  <div data-id="element-271">
                    <h3 className="text-3xl font-bold text-white" data-id="element-272">{date}</h3>
                    <p className="text-slate-400 text-lg" data-id="element-273">
                      {events.length} {events.length === 1 ? 'event' : 'events'}{' '}
                      scheduled
                    </p>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-cyan-500 via-violet-500 to-pink-500 opacity-30" data-id="element-274"></div>
              </div>

              {/* Events for this day */}
              <div className="space-y-12" data-id="element-275">
                {events.map((event, eventIndex) => <ItineraryCard key={eventIndex} event={event} index={eventIndex} data-id="element-276" />)}
              </div>

              {/* Day separator (except for last day) */}
              {dayIndex < Object.keys(eventsByDate).length - 1 && <div className="mt-24 mb-12" data-id="element-277">
                  <div className="relative" data-id="element-278">
                    <div className="absolute inset-0 flex items-center" data-id="element-279">
                      <div className="w-full border-t-2 border-white/10" data-id="element-280"></div>
                    </div>
                    <div className="relative flex justify-center" data-id="element-281">
                      <span className="bg-slate-900 px-6 py-2 text-slate-500 text-sm font-semibold rounded-full border border-white/10" data-id="element-282">
                        Next Day
                      </span>
                    </div>
                  </div>
                </div>}
            </div>)}

          {/* Special card for Feb 4 with multiple images */}
          <CityTourCard data-id="element-283" />
        </div>
      </section>

      {/* Closing section */}
      <div className="mt-16 text-center" data-id="element-284">
        <div className="bg-gradient-to-r from-cyan-500/10 via-violet-500/10 to-pink-500/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10" data-id="element-285">
          <h3 className="text-2xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-286">
            Ready for the Experience?
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto [text-wrap:pretty]" data-id="element-287">
            This carefully curated itinerary combines strategic business
            development with cultural immersion, designed to maximize your
            impact and connections in Qatar's innovation ecosystem.
          </p>
        </div>
      </div>
    </div>;
}
function QatarEcosystem() {
  return <div className="space-y-16" data-id="element-288">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-289">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-6" data-id="element-290">
          <GlobeIcon className="w-4 h-4 text-amber-300" data-id="element-291" />
          <span className="text-sm font-semibold text-amber-300" data-id="element-292">
            Strategic Partnerships
          </span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-293">
          Qatar Innovation Ecosystem
        </h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto [text-wrap:balance]" data-id="element-294">
          Connecting with global investors and strategic partners across MENA
          and beyond
        </p>
      </div>

      {/* Investor & Stakeholder Context */}
      <section data-id="element-295">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-296">
          <div className="flex items-center gap-3 mb-6" data-id="element-297">
            <div className="w-1 h-8 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full" data-id="element-298" />
            <h2 className="text-2xl font-bold text-white" data-id="element-299">
              Investors & Strategic Partners
            </h2>
          </div>
          <div className="space-y-4 text-slate-300 leading-relaxed text-lg" data-id="element-300">
            <p className="[text-wrap:pretty]" data-id="element-301">
              The investors and stakeholders we have scheduled to meet are a
              highly curated group of global investors representing leading
              venture capital firms, corporate venture arms, and strategic
              investment platforms across North America, Europe, MENA, Africa,
              and Asia. Collectively, these investors bring deep experience in
              deploying institutional capital, scaling technology companies from
              early traction through growth stages, and supporting founders
              across complex, cross-border markets.
            </p>
            <p className="[text-wrap:pretty]" data-id="element-302">
              This investor group offers more than capital—they provide
              strategic insight, market access, and long-term partnership
              potential. With expertise spanning fintech, health and medtech,
              B2B SaaS, AI, climate and energy, workforce technologies, and
              industrial innovation, they are uniquely positioned to support
              women-led companies building category-defining solutions with
              global relevance. Together, they represent conviction in the
              FoundHers mission and the next generation of scalable, women-led
              innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Investor Profiles */}
      <section data-id="element-303">
        <div className="flex items-center gap-3 mb-12" data-id="element-304">
          <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-305" />
          <h2 className="text-2xl font-bold text-white" data-id="element-306">
            Investors to be on your radar (some may appear at demo day)
          </h2>
        </div>

        <div className="space-y-16" data-id="element-307">
          {investors.map((investor, index) => <InvestorCard key={index} investor={investor} index={index} data-id="element-308" />)}
        </div>
      </section>

      {/* Key Ecosystem Pillars */}
      <section className="mt-20" data-id="element-309">
        <div className="flex items-center gap-3 mb-8" data-id="element-310">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-311" />
          <h2 className="text-2xl font-bold text-white" data-id="element-312">Ecosystem Pillars</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-313">
          {/* Venture Capital */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-314">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4" data-id="element-315">
              <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-316" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-317">
              Venture Capital
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-318">
              Leading VC firms with deep expertise in scaling technology
              companies across global markets
            </p>
          </div>

          {/* Corporate Venture */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-319">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4" data-id="element-320">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-321" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-322">
              Corporate Innovation
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-323">
              Strategic corporate partners offering market access and
              partnership opportunities
            </p>
          </div>

          {/* Government & Development */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-324">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4" data-id="element-325">
              <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-326" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-327">
              Government & Development
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-328">
              Public-private partnerships and development banks supporting
              regional growth
            </p>
          </div>

          {/* Global Networks */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-329">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4" data-id="element-330">
              <GlobeIcon className="w-6 h-6 text-white" data-id="element-331" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-332">
              Global Networks
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-333">
              Cross-border connections spanning North America, Europe, MENA,
              Africa, and Asia
            </p>
          </div>
        </div>
      </section>

      {/* Closing section */}
      <div className="mt-16 text-center" data-id="element-334">
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-pink-500/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10" data-id="element-335">
          <h3 className="text-2xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-336">
            Building Global Partnerships
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto [text-wrap:pretty]" data-id="element-337">
            This ecosystem represents a unique convergence of capital,
            expertise, and strategic support designed to accelerate women-led
            innovation on a global scale.
          </p>
        </div>
      </div>
    </div>;
}
function CulturalAwareness() {
  return <div className="space-y-16" data-id="element-338">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-339">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-6" data-id="element-340">
          <SparklesIcon className="w-4 h-4 text-amber-300" data-id="element-341" />
          <span className="text-sm font-semibold text-amber-300" data-id="element-342">
            Essential Guide
          </span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-343">
          Cultural Awareness
        </h2>
        <p className="text-xl text-slate-300 max-w-3xl mx-auto [text-wrap:balance]" data-id="element-344">
          Understanding local customs and cultural expectations will help you
          navigate meetings, social interactions, and daily life in Doha with
          confidence and respect
        </p>
      </div>

      {/* Introduction */}
      <section data-id="element-345">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-346">
          <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-347">
            The guidance below is designed to support a smooth, productive, and
            enriching trip.
          </p>
        </div>
      </section>

      {/* 1. Attire & Modesty */}
      <section data-id="element-348">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-349">
          <div className="flex items-center gap-3 mb-6" data-id="element-350">
            <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-rose-500 rounded-full" data-id="element-351" />
            <h3 className="text-2xl font-bold text-white" data-id="element-352">
              1. Attire & Modesty
            </h3>
          </div>

          <div className="space-y-6" data-id="element-353">
            <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-354">
              In Doha, modest dress is expected in professional settings and
              public spaces. While Qatar is modern and international, cultural
              norms place value on respectful and conservative attire.
            </p>

            <div data-id="element-355">
              <h4 className="text-xl font-semibold text-white mb-4" data-id="element-356">
                General guidelines:
              </h4>
              <ul className="space-y-3" data-id="element-357">
                <li className="flex items-start gap-3" data-id="element-358">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-359" />
                  <span className="text-slate-300 text-lg" data-id="element-360">
                    Clothing should cover shoulders, upper arms, and knees
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-361">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-362" />
                  <span className="text-slate-300 text-lg" data-id="element-363">
                    Avoid low-cut, sheer, or overly fitted garments
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-364">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-365" />
                  <span className="text-slate-300 text-lg" data-id="element-366">
                    Lightweight, breathable fabrics are ideal due to the climate
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-367">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-368" />
                  <span className="text-slate-300 text-lg" data-id="element-369">
                    Business settings typically lean formal and conservative
                  </span>
                </li>
              </ul>
            </div>

            <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-370">
              For women, long dresses, tailored trousers with loose blouses, or
              abayas are widely appropriate. Men are encouraged to wear long
              trousers and collared shirts; suits are standard for formal
              meetings.
            </p>
          </div>
        </div>
      </section>

      {/* Modest Fashion Support */}
      <section data-id="element-371">
        <div className="relative" data-id="element-372">
          <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-rose-400 to-orange-500 rounded-3xl blur opacity-75" data-id="element-373"></div>
          <div className="relative bg-slate-900 border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-374">
            <div className="flex items-center gap-3 mb-6" data-id="element-375">
              <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-rose-500 rounded-full" data-id="element-376" />
              <h3 className="text-2xl font-bold text-white" data-id="element-377">
                Modest Fashion Support (Women)
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-378">
              <div className="space-y-6" data-id="element-379">
                <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-380">
                  <span className="text-pink-300 font-semibold" data-id="element-381">
                    WeAre WEAR
                  </span>{' '}
                  offers thoughtfully curated modest clothing options for women
                  traveling to the region, with a focus on elegance, comfort,
                  and cultural respect. Through the platform, travelers can
                  explore beautifully designed pieces such as abayas and other
                  modest garments created by local designers in Qatar.
                </p>

                <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-382">
                  These pieces are suitable for formal meetings, cultural
                  visits, and everyday wear, allowing women to feel
                  appropriately dressed while remaining personally expressive.
                </p>

                <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-id="element-383">
                  <h4 className="text-lg font-semibold text-white mb-3" data-id="element-384">
                    Rental Option Available
                  </h4>
                  <p className="text-slate-300 leading-relaxed [text-wrap:pretty] mb-4" data-id="element-385">
                    For those seeking flexibility and sustainability, WeAre WEAR
                    provides a rental option, making it easier to wear premium
                    designs without committing to full purchases. Rental prices
                    typically begin at{' '}
                    <span className="text-pink-300 font-semibold" data-id="element-386">250+</span>,
                    offering access to high-quality abayas that are often costly
                    to buy outright.
                  </p>
                  <p className="text-slate-400 text-sm italic" data-id="element-387">
                    This circular fashion model supports local designers while
                    promoting conscious consumption.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4" data-id="element-388">
                  <a href="https://www.wearewear.ai/shop" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-pink-500/50" data-id="element-389">
                    <GlobeIcon className="w-5 h-5" data-id="element-390" />
                    <span data-id="element-391">Browse Shop</span>
                  </a>
                  <a href="mailto:nouka@wearewear.ai" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition-all duration-300" data-id="element-392">
                    <span data-id="element-393">Contact: nouka@wearewear.ai</span>
                  </a>
                </div>
              </div>

              <div className="relative group" data-id="element-394">
                <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-395"></div>
                <div className="relative rounded-2xl overflow-hidden" data-id="element-396">
                  <img src="https://cdn.magicpatterns.com/uploads/azxxgVghMDFa7BJjPb5Lws/WeAreWearAI.jpg" alt="WeAre WEAR" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-397" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Customs When Engaging */}
      <section data-id="element-398">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-399">
          <div className="flex items-center gap-3 mb-6" data-id="element-400">
            <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full" data-id="element-401" />
            <h3 className="text-2xl font-bold text-white" data-id="element-402">
              2. Customs When Engaging with Qatari Business Professionals
            </h3>
          </div>

          <ul className="space-y-4" data-id="element-403">
            <li className="flex items-start gap-3" data-id="element-404">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-405" />
              <span className="text-slate-300 text-lg" data-id="element-406">
                Greetings are formal; a polite handshake is common (wait for
                cues when greeting men)
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-407">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-408" />
              <span className="text-slate-300 text-lg" data-id="element-409">
                Titles and surnames are preferred unless invited otherwise
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-410">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-411" />
              <span className="text-slate-300 text-lg" data-id="element-412">
                Meetings may begin with informal conversation—this is
                intentional and relationship-driven
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-413">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-414" />
              <span className="text-slate-300 text-lg" data-id="element-415">
                Patience is valued; decision-making may take time
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-416">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-417" />
              <span className="text-slate-300 text-lg" data-id="element-418">
                Respect for hierarchy and seniority is important
              </span>
            </li>
          </ul>

          <div className="mt-6 p-6 bg-cyan-500/10 rounded-2xl border border-cyan-500/30" data-id="element-419">
            <p className="text-cyan-200 text-lg [text-wrap:pretty]" data-id="element-420">
              Demonstrating cultural awareness, courtesy, and genuine interest
              in Qatar will go a long way in building trust.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Making the Most of the Trip */}
      <section data-id="element-421">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-422">
          <div className="flex items-center gap-3 mb-6" data-id="element-423">
            <div className="w-1 h-8 bg-gradient-to-b from-violet-400 to-purple-500 rounded-full" data-id="element-424" />
            <h3 className="text-2xl font-bold text-white" data-id="element-425">
              3. Making the Most of the Trip
            </h3>
          </div>

          <ul className="space-y-4" data-id="element-426">
            <li className="flex items-start gap-3" data-id="element-427">
              <SparklesIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-428" />
              <span className="text-slate-300 text-lg" data-id="element-429">
                Be open to relationship-building beyond scheduled meetings
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-430">
              <SparklesIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-431" />
              <span className="text-slate-300 text-lg" data-id="element-432">
                Attend social and networking events when possible
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-433">
              <SparklesIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-434" />
              <span className="text-slate-300 text-lg" data-id="element-435">
                Ask thoughtful questions about Qatar's vision, priorities, and
                ecosystem
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-436">
              <SparklesIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-437" />
              <span className="text-slate-300 text-lg" data-id="element-438">
                Allow flexibility in your schedule
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-439">
              <SparklesIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-440" />
              <span className="text-slate-300 text-lg" data-id="element-441">
                Observe, listen, and adapt—cultural intelligence is a key asset
              </span>
            </li>
          </ul>

          <div className="mt-8 p-8 bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl border border-violet-500/30" data-id="element-442">
            <p className="text-xl text-white font-semibold text-center [text-wrap:balance]" data-id="element-443">
              Approaching the trip with curiosity, respect, and preparation will
              enhance both professional outcomes and personal experience.
            </p>
          </div>
        </div>
      </section>
    </div>;
}
export function DohaDocument() {
  const [activeTab, setActiveTab] = useState('growth-labs');
  // Set document title for SEO
  useEffect(() => {
    document.title = 'FoundHers Doha 2026';
  }, []);
  return <main className="min-h-screen w-full relative overflow-hidden" data-id="element-444">
      {/* Background Image with Purple Fade */}
      <div className="fixed inset-0 z-0" data-id="element-445">
        <img src="https://cdn.magicpatterns.com/uploads/x21oDNBHunJvXPHouZZXyx/Screenshot_2026-01-12_at_3.49.08_PM.png" alt="Doha Skyline" className="w-full h-full object-cover" data-id="element-446" />
        {/* Purple gradient overlay that fades out the image */}
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/95 via-violet-950/90 to-slate-900/95" data-id="element-447"></div>
      </div>

      {/* Existing decorative elements */}
      <div className="relative z-10" data-id="element-448">
        <GeometricPattern data-id="element-449" />
        <GeometricPatternLeft data-id="element-450" />

        {/* Ambient glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl" data-id="element-451" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl" data-id="element-452" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-id="element-453">
        {/* Header Section */}
        <header className="text-center mb-16" data-id="element-454">
          {/* Logo */}
          <div className="flex justify-center mb-8" data-id="element-455">
            <div className="w-32 h-32" data-id="element-456">
              <img src="https://cdn.magicpatterns.com/uploads/f4wwvtUhSALA8cYNSKZT5L/FOUNDHERS_logo_white.png" alt="FoundHers+ Logo" className="w-full h-full object-contain" data-id="element-457" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-id="element-458">
            FoundHers+ Growth Labs
          </h1>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-pink-500/20 border border-cyan-500/30 mb-6" data-id="element-459">
            <span className="text-cyan-300 font-semibold" data-id="element-460">
              Doha Strategic Programming
            </span>
          </div>

          {/* Subtitle - Fixed widow words */}
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed [text-wrap:balance]" data-id="element-461">
            Preparing Women Founders for Investment, Market Expansion & Global
            Engagement
          </p>
        </header>

        {/* Tab Navigation - Centered */}
        <nav className="mb-16 flex justify-center" data-id="element-462">
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-2 inline-flex gap-2 flex-wrap" data-id="element-463">
            <button onClick={() => setActiveTab('growth-labs')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'growth-labs' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-id="element-464">
              Growth Labs
            </button>
            <button onClick={() => setActiveTab('delegation')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'delegation' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-id="element-465">
              Delegation
            </button>
            <button onClick={() => setActiveTab('itinerary')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'itinerary' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-id="element-466">
              Itinerary
            </button>
            <button onClick={() => setActiveTab('cultural-awareness')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'cultural-awareness' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-id="element-467">
              Cultural Awareness
            </button>
            <button onClick={() => setActiveTab('ecosystem')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'ecosystem' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-id="element-468">
              Qatar Ecosystem
            </button>
          </div>
        </nav>

        {/* Tab Content */}
        {activeTab === 'growth-labs' && <>
            {/* Program Context */}
            <section className="mb-16" data-id="element-469">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-470">
                <div className="flex items-center gap-3 mb-6" data-id="element-471">
                  <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-472" />
                  <h2 className="text-2xl font-bold text-white" data-id="element-473">
                    Program Overview
                  </h2>
                </div>
                <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-474">
                  FoundHers+ Growth Labs is an intensive global growth
                  initiative and power sprint accelerator/bootcamp designed over
                  a few weeks to get the founders in the mindset for
                  fundraising, international expansion, and high-impact
                  engagement across global innovation ecosystems. The Doha,
                  Qatar experience combines investor readiness, cultural
                  intelligence, and strategic market exposure aligned with the
                  Web Summit and MENA opportunity landscape.
                </p>
              </div>
            </section>

            {/* Activities Schedule */}
            <section className="mb-16" data-id="element-475">
              <div className="flex items-center gap-3 mb-8" data-id="element-476">
                <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-477" />
                <h2 className="text-2xl font-bold text-white" data-id="element-478">
                  Upcoming Activities & Strategic Sessions
                </h2>
              </div>

              <div className="space-y-6" data-id="element-479">
                {activities.map((activity, index) => <ActivityCard key={index} activity={activity} index={index} data-id="element-480" />)}
              </div>
            </section>

            {/* Strategic Outcomes */}
            <section className="mb-16" data-id="element-481">
              <div className="flex items-center gap-3 mb-8" data-id="element-482">
                <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-pink-500 rounded-full" data-id="element-483" />
                <h2 className="text-2xl font-bold text-white" data-id="element-484">
                  Strategic Outcomes
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-485">
                {outcomes.map((outcome, index) => <OutcomeCard key={index} outcome={outcome} data-id="element-486" />)}
              </div>
            </section>
          </>}

        {activeTab === 'ecosystem' && <QatarEcosystem data-id="element-487" />}

        {activeTab === 'delegation' && <FoundHersDelegation data-id="element-488" />}

        {activeTab === 'itinerary' && <DohaItinerary data-id="element-489" />}

        {activeTab === 'cultural-awareness' && <CulturalAwareness data-id="element-490" />}

        {/* Footer */}
        <footer className="border-t border-white/10 pt-10 mt-16" data-id="element-491">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-492">
            {/* Logo and tagline */}
            <div className="flex items-center gap-4" data-id="element-493">
              <div className="w-16 h-16" data-id="element-494">
                <img src="https://cdn.magicpatterns.com/uploads/f4wwvtUhSALA8cYNSKZT5L/FOUNDHERS_logo_white.png" alt="FoundHers+ Logo" className="w-full h-full object-contain" data-id="element-495" />
              </div>
              <div data-id="element-496">
                <p className="text-white font-semibold" data-id="element-497">FoundHers+</p>
                <p className="text-sm text-slate-400 [text-wrap:balance]" data-id="element-498">
                  Building globally competitive women-led ventures
                </p>
              </div>
            </div>

            {/* Location and date */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10" data-id="element-499">
              <GlobeIcon className="w-4 h-4 text-cyan-400" data-id="element-500" />
              <span className="text-sm text-slate-300" data-id="element-501">
                Doha, Qatar | January 2026
              </span>
            </div>
          </div>
        </footer>
      </div>
    </main>;
}